import React, { useState, useCallback } from 'react';
import { API_URL, US_STATES, S, maskDOB, Fld, Inp, Sel, DocDone } from './ui';

const SCR_STEPS = ['You', 'Other names', 'Household', 'Address history'];
const YEARS_REQUIRED = 28;

const SEX_OPTIONS = ['Male', 'Female'];
const toSexCode = (v) => (v === 'Male' ? 'M' : v === 'Female' ? 'F' : '');

function maskMoYr(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 6);
  return d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
}

// Months since year zero — lets periods be compared as plain numbers.
function monthIndex(moYr) {
  const m = /^(\d{2})\/(\d{4})$/.exec(moYr || '');
  if (!m) return null;
  const month = Number(m[1]);
  if (month < 1 || month > 12) return null;
  return Number(m[2]) * 12 + month;
}

function fromMonthIndex(idx) {
  const year = Math.floor((idx - 1) / 12);
  const month = idx - year * 12;
  return `${String(month).padStart(2, '0')}/${year}`;
}

function currentMonth() {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
}

// The SCR wants 28 years of history, but never asks for time before the
// applicant was born — for a 24-year-old the birth date is the real floor.
function requiredBackTo(dob) {
  const now = new Date();
  const cutoff = (now.getFullYear() - YEARS_REQUIRED) * 12 + (now.getMonth() + 1);
  const d = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dob || '');
  const birth = d ? Number(d[3]) * 12 + Number(d[1]) : null;
  return birth ? Math.max(cutoff, birth) : cutoff;
}

const blankPerson = () => ({ last_name: '', first_name: '', sex: '', dob: '' });
const blankMember = () => ({ ...blankPerson(), relationship: '' });
const blankAddress = () => ({ street: '', apt: '', city: '', state: '', zip: '', from: '' });

export default function ScrTask({ task, token, session, onBack, onComplete }) {
  const [step, setStep] = useState(0);
  const [applicant, setApplicant] = useState({
    last_name: session?.last_name || '',
    first_name: session?.first_name || '',
    sex: '',
    dob: '',
  });
  const [noOtherNames, setNoOtherNames] = useState(true);
  const [otherNames, setOtherNames] = useState([]);
  const [noHousehold, setNoHousehold] = useState(false);
  const [members, setMembers] = useState([blankMember()]);
  const [addresses, setAddresses] = useState([blankAddress()]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const clearError = useCallback((key) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
  }, []);

  const setApplicantField = (key, val) => {
    setApplicant((prev) => ({ ...prev, [key]: val }));
    clearError(`applicant_${key}`);
  };

  const updateList = (setter, idx, key, val, errPrefix) => {
    setter((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: val } : row)));
    clearError(`${errPrefix}_${idx}_${key}`);
  };

  // ── Address chain ────────────────────────────────────────────────────────
  // Row 0 is the current address and runs to today. Every other row ends where
  // the row above it began, so the history cannot contain a gap — which is the
  // single most common reason the SCR sends a form back.
  const endDateFor = (idx) => (idx === 0 ? currentMonth() : addresses[idx - 1].from);

  const backTo = requiredBackTo(applicant.dob);
  const earliest = addresses.length
    ? monthIndex(addresses[addresses.length - 1].from)
    : null;
  const covered = earliest !== null && earliest <= backTo;

  // ── Validation ───────────────────────────────────────────────────────────
  function validate() {
    const e = {};

    if (step === 0) {
      if (!applicant.last_name.trim()) e.applicant_last_name = 'Required';
      if (!applicant.first_name.trim()) e.applicant_first_name = 'Required';
      if (!applicant.sex) e.applicant_sex = 'Required';
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(applicant.dob)) e.applicant_dob = 'Use MM/DD/YYYY';
    }

    if (step === 1 && !noOtherNames) {
      if (otherNames.length === 0) e.other_names = 'Add a name, or tick the box above';
      otherNames.forEach((n, i) => {
        if (!n.last_name.trim()) e[`other_${i}_last_name`] = 'Required';
        if (!n.first_name.trim()) e[`other_${i}_first_name`] = 'Required';
      });
    }

    if (step === 2 && !noHousehold) {
      if (members.length === 0) e.members = 'Add a person, or tick the box above';
      members.forEach((m, i) => {
        if (!m.relationship.trim()) e[`member_${i}_relationship`] = 'Required';
        if (!m.last_name.trim()) e[`member_${i}_last_name`] = 'Required';
        if (!m.first_name.trim()) e[`member_${i}_first_name`] = 'Required';
        if (!m.sex) e[`member_${i}_sex`] = 'Required';
        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(m.dob)) e[`member_${i}_dob`] = 'Use MM/DD/YYYY';
      });
    }

    if (step === 3) {
      addresses.forEach((a, i) => {
        if (!a.street.trim()) e[`addr_${i}_street`] = 'Required';
        if (!a.city.trim()) e[`addr_${i}_city`] = 'Required';
        if (!a.state) e[`addr_${i}_state`] = 'Required';
        if (a.zip.length !== 5) e[`addr_${i}_zip`] = '5-digit ZIP required';
        const idx = monthIndex(a.from);
        if (idx === null) e[`addr_${i}_from`] = 'Use MM/YYYY';
        else {
          const end = monthIndex(endDateFor(i));
          if (end !== null && idx > end) e[`addr_${i}_from`] = 'Starts after it ended';
        }
      });
      if (!covered) e.coverage = `History must reach back to ${fromMonthIndex(backTo)}`;
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (validate()) setStep((s) => Math.min(s + 1, SCR_STEPS.length - 1));
  }

  async function generate() {
    if (!validate()) return;
    setLoading(true);
    setApiError('');
    try {
      const payload = {
        token,
        applicant: { ...applicant, sex: toSexCode(applicant.sex) },
        maiden_names: noOtherNames
          ? []
          : otherNames.map((n) => ({
              ...n,
              sex: toSexCode(n.sex || applicant.sex),
              dob: n.dob || applicant.dob,
            })),
        no_other_household_members: noHousehold,
        household_members: noHousehold
          ? []
          : members.map((m) => ({ ...m, sex: toSexCode(m.sex) })),
        addresses: addresses.map((a, i) => ({
          street: a.street,
          apt: a.apt,
          city: a.city,
          state: a.state,
          zip: a.zip,
          from: a.from,
          to: i === 0 ? '' : addresses[i - 1].from,
        })),
      };
      const res = await fetch(`${API_URL}/api/onboarding/submit-scr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Error: ${res.status}`);
      }
      await res.json();
      setDone(true);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <DocDone
        title="SCR Form"
        taskId={task.id}
        token={token}
        onDone={() => onComplete(task.id)}
      />
    );
  }

  // ── Step bodies ──────────────────────────────────────────────────────────
  // Plain functions called inline, never mounted as <Component />. Defining a
  // component inside another and using it as JSX makes React remount the whole
  // subtree on every keystroke, which drops focus from the field being typed in.

  function personFields(row, idx, setter, prefix, withRelationship) {
    return (
      <>
        {withRelationship && (
          <Fld label="Relationship to you" error={errors[`${prefix}_${idx}_relationship`]}>
            <Inp
              value={row.relationship}
              onChange={(v) => updateList(setter, idx, 'relationship', v, prefix)}
              error={errors[`${prefix}_${idx}_relationship`]}
              placeholder="Spouse, son, daughter, mother, friend…"
            />
          </Fld>
        )}
        <div style={S.row2}>
          <Fld label="Last name" error={errors[`${prefix}_${idx}_last_name`]}>
            <Inp
              value={row.last_name}
              onChange={(v) => updateList(setter, idx, 'last_name', v, prefix)}
              error={errors[`${prefix}_${idx}_last_name`]}
            />
          </Fld>
          <Fld label="First name" error={errors[`${prefix}_${idx}_first_name`]}>
            <Inp
              value={row.first_name}
              onChange={(v) => updateList(setter, idx, 'first_name', v, prefix)}
              error={errors[`${prefix}_${idx}_first_name`]}
            />
          </Fld>
        </div>
        <div style={S.row2}>
          <Fld label="Sex" error={errors[`${prefix}_${idx}_sex`]}>
            <Sel
              value={row.sex}
              onChange={(v) => updateList(setter, idx, 'sex', v, prefix)}
              error={errors[`${prefix}_${idx}_sex`]}
              options={SEX_OPTIONS}
            />
          </Fld>
          <Fld label="Date of birth" error={errors[`${prefix}_${idx}_dob`]}>
            <Inp
              value={row.dob}
              onChange={(v) => updateList(setter, idx, 'dob', maskDOB(v), prefix)}
              error={errors[`${prefix}_${idx}_dob`]}
              placeholder="MM/DD/YYYY"
            />
          </Fld>
        </div>
      </>
    );
  }

  function stepYou() {
    return (
      <>
        <div style={S.sec}>About you</div>
        <div style={S.row2}>
          <Fld label="Last name" error={errors.applicant_last_name}>
            <Inp
              value={applicant.last_name}
              onChange={(v) => setApplicantField('last_name', v)}
              error={errors.applicant_last_name}
            />
          </Fld>
          <Fld label="First name" error={errors.applicant_first_name}>
            <Inp
              value={applicant.first_name}
              onChange={(v) => setApplicantField('first_name', v)}
              error={errors.applicant_first_name}
            />
          </Fld>
        </div>
        <div style={S.row2}>
          <Fld label="Sex" error={errors.applicant_sex}>
            <Sel
              value={applicant.sex}
              onChange={(v) => setApplicantField('sex', v)}
              error={errors.applicant_sex}
              options={SEX_OPTIONS}
            />
          </Fld>
          <Fld label="Date of birth" error={errors.applicant_dob}>
            <Inp
              value={applicant.dob}
              onChange={(v) => setApplicantField('dob', maskDOB(v))}
              error={errors.applicant_dob}
              placeholder="MM/DD/YYYY"
            />
          </Fld>
        </div>
      </>
    );
  }

  function stepOtherNames() {
    return (
      <>
        <div style={S.sec}>Other names</div>
        <div style={{ ...S.sub, marginTop: 0, marginBottom: '1rem' }}>
          Any maiden name, previous married name or alias you have been known by.
        </div>
        <label style={S.chk}>
          <input
            type="checkbox"
            checked={noOtherNames}
            onChange={(ev) => {
              setNoOtherNames(ev.target.checked);
              clearError('other_names');
              if (!ev.target.checked && otherNames.length === 0) {
                setOtherNames([blankPerson()]);
              }
            }}
          />
          I have never been known by any other name
        </label>

        {!noOtherNames && (
          <div style={{ marginTop: '1rem' }}>
            {errors.other_names && <div style={S.err}>{errors.other_names}</div>}
            {otherNames.map((n, i) => (
              <div key={i} style={S.subCard}>
                <div style={S.subHdr}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                    Name {i + 1}
                  </div>
                  <button
                    style={S.rmBtn}
                    onClick={() => setOtherNames((prev) => prev.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                </div>
                <div style={S.row2}>
                  <Fld label="Last name" error={errors[`other_${i}_last_name`]}>
                    <Inp
                      value={n.last_name}
                      onChange={(v) => updateList(setOtherNames, i, 'last_name', v, 'other')}
                      error={errors[`other_${i}_last_name`]}
                    />
                  </Fld>
                  <Fld label="First name" error={errors[`other_${i}_first_name`]}>
                    <Inp
                      value={n.first_name}
                      onChange={(v) => updateList(setOtherNames, i, 'first_name', v, 'other')}
                      error={errors[`other_${i}_first_name`]}
                    />
                  </Fld>
                </div>
              </div>
            ))}
            <button
              style={S.addBtn}
              onClick={() => setOtherNames((prev) => [...prev, blankPerson()])}
            >
              + Add another name
            </button>
          </div>
        )}
      </>
    );
  }

  function stepHousehold() {
    return (
      <>
        <div style={S.sec}>Household members</div>
        <div style={{ ...S.sub, marginTop: 0, marginBottom: '1rem' }}>
          Everyone living in your home at present — adults and children, related
          to you or not.
        </div>
        <label style={S.chk}>
          <input
            type="checkbox"
            checked={noHousehold}
            onChange={(ev) => {
              setNoHousehold(ev.target.checked);
              clearError('members');
              if (!ev.target.checked && members.length === 0) setMembers([blankMember()]);
            }}
          />
          I live alone — there are no other household members
        </label>

        {!noHousehold && (
          <div style={{ marginTop: '1rem' }}>
            {errors.members && <div style={S.err}>{errors.members}</div>}
            {members.map((m, i) => (
              <div key={i} style={S.subCard}>
                <div style={S.subHdr}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                    Person {i + 1}
                  </div>
                  <button
                    style={S.rmBtn}
                    onClick={() => setMembers((prev) => prev.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                </div>
                {personFields(m, i, setMembers, 'member', true)}
              </div>
            ))}
            <button
              style={S.addBtn}
              onClick={() => setMembers((prev) => [...prev, blankMember()])}
            >
              + Add another person
            </button>
          </div>
        )}
      </>
    );
  }

  function addressCard(a, i) {
    const isCurrent = i === 0;
    return (
      <div key={i} style={S.subCard}>
        <div style={S.subHdr}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
            {isCurrent ? 'Current address' : `Previous address ${i}`}
          </div>
          {!isCurrent && (
            <button
              style={S.rmBtn}
              onClick={() => setAddresses((prev) => prev.filter((_, j) => j !== i))}
            >
              Remove
            </button>
          )}
        </div>

        <Fld label="Street address" error={errors[`addr_${i}_street`]}>
          <Inp
            value={a.street}
            onChange={(v) => updateList(setAddresses, i, 'street', v, 'addr')}
            error={errors[`addr_${i}_street`]}
            placeholder="Number and street — no PO boxes"
          />
        </Fld>

        <div style={S.row3}>
          <Fld label="City" error={errors[`addr_${i}_city`]}>
            <Inp
              value={a.city}
              onChange={(v) => updateList(setAddresses, i, 'city', v, 'addr')}
              error={errors[`addr_${i}_city`]}
            />
          </Fld>
          <Fld label="State" error={errors[`addr_${i}_state`]}>
            <Sel
              value={a.state}
              onChange={(v) => updateList(setAddresses, i, 'state', v, 'addr')}
              error={errors[`addr_${i}_state`]}
              options={US_STATES}
            />
          </Fld>
          <Fld label="Apt #">
            <Inp
              value={a.apt}
              onChange={(v) => updateList(setAddresses, i, 'apt', v, 'addr')}
            />
          </Fld>
        </div>

        <div style={S.row3}>
          <Fld label="ZIP" error={errors[`addr_${i}_zip`]}>
            <Inp
              value={a.zip}
              onChange={(v) =>
                updateList(setAddresses, i, 'zip', v.replace(/\D/g, '').slice(0, 5), 'addr')
              }
              error={errors[`addr_${i}_zip`]}
            />
          </Fld>
          <Fld label="Moved in" error={errors[`addr_${i}_from`]}>
            <Inp
              value={a.from}
              onChange={(v) => updateList(setAddresses, i, 'from', maskMoYr(v), 'addr')}
              error={errors[`addr_${i}_from`]}
              placeholder="MM/YYYY"
            />
          </Fld>
          <Fld label="Moved out">
            <Inp
              value={isCurrent ? 'Present' : endDateFor(i) || '—'}
              onChange={() => {}}
              readOnly
              style={{ background: '#f3f4f6', color: '#6b7280' }}
            />
          </Fld>
        </div>
      </div>
    );
  }

  function stepAddresses() {
    return (
      <>
        <div style={S.sec}>Where you have lived</div>
        <div style={{ ...S.sub, marginTop: 0, marginBottom: '1rem' }}>
          Start with where you live now and work backwards. Each move-out date is
          filled in for you from the address below it, so there are no gaps.
        </div>

        {addresses.map((a, i) => addressCard(a, i))}

        <div
          style={{
            ...S.cardSm,
            marginTop: '1rem',
            marginBottom: 0,
            background: covered ? '#f0fdf4' : '#fffbeb',
            border: `1px solid ${covered ? '#bbf7d0' : '#fde68a'}`,
            fontSize: 13,
            color: covered ? '#16a34a' : '#92400e',
          }}
        >
          {covered
            ? 'Your history covers the full period the SCR asks for.'
            : earliest
            ? `Covered back to ${fromMonthIndex(earliest)}. Keep adding earlier addresses until you reach ${fromMonthIndex(backTo)}.`
            : `Add addresses going back to ${fromMonthIndex(backTo)}.`}
        </div>
        {errors.coverage && <div style={S.err}>{errors.coverage}</div>}

        {!covered && (
          <button
            style={S.addBtn}
            onClick={() => setAddresses((prev) => [...prev, blankAddress()])}
          >
            + Add earlier address
          </button>
        )}
      </>
    );
  }

  const stepBodies = [stepYou, stepOtherNames, stepHousehold, stepAddresses];
  const isLast = step === SCR_STEPS.length - 1;

  return (
    <div style={S.page}>
      <button style={{ ...S.rmBtn, marginBottom: '1rem' }} onClick={onBack}>
        ← Back to tasks
      </button>

      <div style={S.card}>
        <div style={{ ...S.h2, marginBottom: 4 }}>SCR Form (LDSS-3370)</div>
        <div style={{ ...S.sub, marginTop: 0, marginBottom: '1.5rem' }}>
          Step {step + 1} of {SCR_STEPS.length} — {SCR_STEPS[step]}
        </div>

        {stepBodies[step]()}

        {Object.values(errors).some(Boolean) && (
          <div style={{ ...S.err, marginTop: '.75rem' }}>
            Please complete the highlighted fields above.
          </div>
        )}
        {apiError && <div style={{ ...S.err, marginTop: '.75rem' }}>{apiError}</div>}

        <div style={S.actions}>
          <button
            style={S.btnBack}
            onClick={() => (step === 0 ? onBack() : setStep((s) => s - 1))}
          >
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          {isLast ? (
            <button style={S.btnGen} onClick={generate} disabled={loading}>
              {loading ? 'Submitting…' : 'Submit to HR'}
            </button>
          ) : (
            <button style={S.btnNext} onClick={next}>
              Continue →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
